import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { Image as ImageIcon, FolderOpen, Package, Star, Pencil, Trash2, Droplet, Plus, GripVertical, BarChart3, Eye, Heart, ToggleLeft, ToggleRight } from "lucide-react";
import {
  getGalleriesByEvent,
  updateEventGallery,
  archiveEventGallery,
  updateGalleryOrder,
  setShareGalleryActive,
} from "../../services/galleryService";
import { getEventMedia } from "../../services/eventDetailService";
import Loader from "../../components/common/Loader";
import Button from "../../components/common/Button";
import Badge from "../../components/common/Badge";
import EmptyState from "../../components/common/EmptyState";
import CreateGalleryModal from "../../components/common/CreateGalleryModal";
import WatermarkSettingsModal from "../../components/common/WatermarkSettingsModal";
import ConfirmModal from "../../components/common/ConfirmModal";
import EditNameModal from "../../components/common/EditNameModal";
import ShareGalleryConsoleModal from "./ShareGalleryConsoleModal";
import SupportBannerModal from "../rooms/SupportBannerModal";
import { color, radius, shadow } from "../../styles/theme";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  rectSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const isShareGallery = (gallery) => gallery?.GalleryType === 3 || gallery?.GalleryType === "ShareGallery";
const isFundraisingGallery = (gallery) => gallery?.IsFundRaising === true;
// The User Gallery is the shared bucket the backend auto-creates for
// participant/Event Host uploads — it's never the Promoter's own content, so
// Edit/rename and Delete stay off-limits here too (see galleryDetail's
// isRestrictedGallery for the matching rule on the albums/media inside it).
const isUserGallery = (gallery) => gallery?.IsUserGallery === true;

const SortableGalleryCard = ({ gallery, onEdit, onDelete, onClick, onOpenRanking, onOpenBanner, onToggleShareActive, canManageGallery, draggable = true }) => {
  const { t } = useTranslation("eventDetails");
  const shareGallery = isShareGallery(gallery);
  const fundraisingGallery = isFundraisingGallery(gallery);
  // Delete/rename are permanently disabled for the Thank You Banner, Share
  // Gallery and User Gallery — those only support activate/deactivate (or
  // nothing at all) via their own lifecycle, never deletion or a name edit.
  const canEditDelete = canManageGallery && !shareGallery && !fundraisingGallery && !isUserGallery(gallery);
  // Share Gallery is rendered with draggable=false and lives outside the
  // DndContext/SortableContext entirely (see MediaTab below) so it can never
  // be dragged and nothing can be dropped above it — useSortable is safe to
  // call unconditionally here regardless (dnd-kit's SortableContext has a
  // fully-formed default value when there's no provider ancestor, confirmed
  // in its source), it just becomes an inert no-op for a pinned card.
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: gallery.Id, disabled: !canManageGallery || !draggable });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={{ ...s.galleryCard, ...style }}
      onClick={() => (fundraisingGallery ? onOpenBanner() : onClick())}
    >
      <div style={{ ...s.colorBar, background: gallery.IsPremium ? color.warning500 : color.primary500 }} />
      <div style={s.galleryBody}>
        <div style={s.galleryTopRow}>
          {canManageGallery && draggable && (
            <span
              {...attributes}
              {...listeners}
              style={s.dragHandle}
              onClick={(e) => e.stopPropagation()}
              title={t("mediaTab.dragToReorder")}
            >
              <GripVertical size={13} strokeWidth={2.25} />
            </span>
          )}
          <span style={s.galleryName} title={gallery.Name}>{gallery.Name}</span>
          <div style={s.galleryActions}>
            {gallery.IsPremium && <Badge variant="warning"><Star size={9} strokeWidth={2.5} /> {t("mediaTab.premiumBadge")}</Badge>}
            {canEditDelete && <button style={s.cardActionBtn} onClick={(e) => { e.stopPropagation(); onEdit(gallery); }} title={t("mediaTab.editGalleryNameTitle")}><Pencil size={12} strokeWidth={2.25} /></button>}
            {canEditDelete && <button style={{ ...s.cardActionBtn, ...s.deleteActionBtn }} onClick={(e) => { e.stopPropagation(); onDelete(gallery); }} title={t("mediaTab.deleteGalleryTitle")}><Trash2 size={12} strokeWidth={2.25} /></button>}
          </div>
        </div>
        <div style={s.galleryCounts}>
          <span style={s.countItem}><ImageIcon size={12} strokeWidth={2} /> {t("mediaTab.mediaCount", { count: gallery.MediaCount })}</span>
          <span style={s.countItem}><FolderOpen size={12} strokeWidth={2} /> {t("mediaTab.albumCount", { count: gallery.AlbumCount })}</span>
        </div>
        {gallery.Package && <div style={s.packageName}><Package size={11} strokeWidth={2} /> {gallery.Package.Name}</div>}
        <div style={s.galleryMeta}>
          {gallery.IsWatermark && <span style={s.tag}>{t("mediaTab.tags.watermark")}</span>}
          {gallery.IsMediaHandlesGallery && <span style={s.tag}>{t("mediaTab.tags.mediaHandles")}</span>}
          {gallery.IsUserGallery && <span style={s.tag}>{t("mediaTab.tags.userGallery")}</span>}
          {fundraisingGallery && <span style={s.tag}>{t("mediaTab.tags.thankYouBanner")}</span>}
          {fundraisingGallery && (
            <Badge variant={gallery.IsFundRaisingActive ? "success" : "neutral"}>
              {gallery.IsFundRaisingActive ? t("mediaTab.activeStatus") : t("mediaTab.deactivatedStatus")}
            </Badge>
          )}
        </div>
        {fundraisingGallery && canManageGallery && (
          <div style={s.shareGalleryActions}>
            <button type="button" className="btn btn-ghost btn-sm" style={s.shareActionButton} onClick={(e) => { e.stopPropagation(); onOpenBanner(); }}>
              <Heart size={13} /> {t("mediaTab.manageBanner")}
            </button>
          </div>
        )}
        {shareGallery && canManageGallery && (
          <div style={s.shareGalleryActions}>
            <button type="button" className="btn btn-ghost btn-sm" style={s.shareActionButton} onClick={(e) => { e.stopPropagation(); onClick(); }}>
              <Eye size={13} /> {t("mediaTab.viewAlbums")}
            </button>
            <button type="button" className="btn btn-secondary btn-sm" style={s.shareActionButton} onClick={(e) => { e.stopPropagation(); onOpenRanking(gallery); }}>
              <BarChart3 size={13} /> {t("mediaTab.rankingSettlement")}
            </button>
            <button type="button" className="btn btn-secondary btn-sm" style={s.shareActionButton} onClick={(e) => { e.stopPropagation(); onToggleShareActive(gallery); }}>
              {gallery.ShareBannerActive ? <ToggleRight size={13} /> : <ToggleLeft size={13} />}
              {gallery.ShareBannerActive ? t("mediaTab.deactivate") : t("mediaTab.activate")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Media tab — recent media grid (new) + galleries CRUD (moved here from the
 * previous single-page EventDetails.jsx, unchanged behavior).
 */
const MediaTab = ({ event, eventId, permissions, onOpenGallery }) => {
  const { t } = useTranslation(["eventDetails", "common"]);
  const [galleries, setGalleries] = useState([]);
  const [recentMedia, setRecentMedia] = useState([]);
  const [loadingGalleries, setLoadingGalleries] = useState(true);
  const [loadingMedia, setLoadingMedia] = useState(true);
  const [showCreateGallery, setShowCreateGallery] = useState(false);
  const [showWatermark, setShowWatermark] = useState(false);
  const [editGalleryTarget, setEditGalleryTarget] = useState(null);
  const [deleteGalleryTarget, setDeleteGalleryTarget] = useState(null);
  const [editGalleryName, setEditGalleryName] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [reorderingGalleries, setReorderingGalleries] = useState(false);
  const [shareGalleryTarget, setShareGalleryTarget] = useState(null);
  const [showBannerModal, setShowBannerModal] = useState(false);
  const [shareActiveTarget, setShareActiveTarget] = useState(null);
  const [shareActiveLoading, setShareActiveLoading] = useState(false);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const refreshGalleries = () => {
    getGalleriesByEvent(eventId)
      .then((res) => setGalleries(res.data?.Result || []))
      .catch(() => toast.error(t("mediaTab.loadGalleriesError")));
  };

  useEffect(() => {
    getGalleriesByEvent(eventId)
      .then((res) => setGalleries(res.data?.Result || []))
      .catch(() => toast.error(t("mediaTab.loadGalleriesError")))
      .finally(() => setLoadingGalleries(false));

    getEventMedia(eventId, { pageNumber: 1, pageSize: 8, orderBy: "desc" })
      .then((res) => setRecentMedia(res.data?.Result || []))
      .catch(() => {}) // recent-media preview is a nice-to-have; fail silently
      .finally(() => setLoadingMedia(false));
  }, [eventId, t]);

  const handleGalleryDragEnd = async (evt) => {
    const { active, over } = evt;
    if (!over || active.id === over.id) return;

    // Share Gallery is excluded from this sortable group entirely (it's
    // rendered outside the SortableContext below), so it's never a drag
    // source/target here. Reorder only the remaining galleries, then always
    // re-pin Share Gallery at the top before persisting, so its position
    // can't be disturbed either visually or in the stored order.
    const pinned = galleries.filter(isShareGallery);
    const sortable = galleries.filter((g) => !isShareGallery(g));
    const oldIndex = sortable.findIndex((g) => g.Id === active.id);
    const newIndex = sortable.findIndex((g) => g.Id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const reordered = [...pinned, ...arrayMove(sortable, oldIndex, newIndex)];
    setGalleries(reordered);

    const orderData = reordered.map((g, i) => ({ id: g.Id, order: i + 1 }));
    setReorderingGalleries(true);
    try {
      await updateGalleryOrder(orderData);
    } catch {
      toast.error(t("mediaTab.saveOrderError"));
      refreshGalleries();
    } finally {
      setReorderingGalleries(false);
    }
  };

  const handleGalleryClick = (gallery) => {
    // The Thank You Banner is a single-image toggle, not a real album/gallery
    // — it must never open the normal Albums page (see handleGalleryClick
    // callers using the fundraising check below in the render).
    onOpenGallery(gallery);
  };

  const handleToggleShareActive = async (gallery) => {
    setShareActiveTarget(gallery);
  };

  const confirmToggleShareActive = async () => {
    if (!shareActiveTarget) return;
    setShareActiveLoading(true);
    try {
      await setShareGalleryActive(shareActiveTarget.Id, eventId, !shareActiveTarget.ShareBannerActive);
      toast.success(shareActiveTarget.ShareBannerActive ? t("mediaTab.shareDeactivated") : t("mediaTab.shareActivated"));
      setShareActiveTarget(null);
      refreshGalleries();
    } catch (err) {
      toast.error(err.response?.data?.Message || t("mediaTab.shareToggleError"));
    } finally {
      setShareActiveLoading(false);
    }
  };

  const openEditGalleryModal = (gallery) => {
    setEditGalleryTarget(gallery);
    setEditGalleryName(gallery?.Name || "");
  };

  const handleEditGallery = async () => {
    const trimmed = editGalleryName.trim();
    if (!trimmed) {
      toast.warning(t("mediaTab.galleryNameRequired"));
      return;
    }
    setActionLoading(true);
    try {
      await updateEventGallery(editGalleryTarget.Id, { EventId: Number(eventId), Name: trimmed });
      toast.success(t("mediaTab.galleryUpdated"));
      setEditGalleryTarget(null);
      refreshGalleries();
    } catch (err) {
      toast.error(err.response?.data?.Message || t("mediaTab.updateGalleryError"));
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteGallery = async () => {
    if (!deleteGalleryTarget) return;
    setActionLoading(true);
    try {
      await archiveEventGallery({ galleryId: deleteGalleryTarget.Id, eventId, name: deleteGalleryTarget.Name });
      toast.success(t("mediaTab.galleryDeleted"));
      setDeleteGalleryTarget(null);
      refreshGalleries();
    } catch (err) {
      toast.error(err.response?.data?.Message || t("mediaTab.deleteGalleryError"));
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div>
      {!loadingMedia && recentMedia.length > 0 && (
        <div style={s.recentSection}>
          <h4 style={s.sectionTitle}>{t("mediaTab.recentMedia")}</h4>
          <div style={s.recentGrid}>
            {recentMedia.map((m, i) => (
              <img
                key={m.Id || i}
                src={m.VideoThumbnail || m.MaxCompressedUrl || m.Url}
                alt=""
                style={s.recentThumb}
                onError={(e) => { e.target.style.visibility = "hidden"; }}
              />
            ))}
          </div>
        </div>
      )}

      <div style={s.sectionHeader}>
        <h3 style={s.sectionTitle}>{t("mediaTab.galleries")}</h3>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {!loadingGalleries && <span style={s.sectionCount}>{t("mediaTab.galleriesFound", { count: galleries.length })}</span>}
          {reorderingGalleries && <span style={s.savingHint}>{t("mediaTab.savingOrder")}</span>}
          {permissions?.canManageGallery && <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowWatermark(true)} title={t("mediaTab.watermarkButtonTitle")}>
            <Droplet size={13} strokeWidth={2.25} /> {t("mediaTab.watermarkButton")}
          </button>}
          {permissions?.canManageGallery && <Button size="sm" icon={Plus} onClick={() => setShowCreateGallery(true)}>{t("mediaTab.newGallery")}</Button>}
        </div>
      </div>

      {loadingGalleries ? (
        <Loader />
      ) : galleries.length === 0 ? (
        <EmptyState icon={ImageIcon} text={t("mediaTab.noGalleries")} />
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleGalleryDragEnd}>
          <div style={s.galleryGrid}>
            {/* Share Gallery must always stay first and can never be
                reordered — pinned outside the sortable group entirely so
                nothing can be dragged above it either. */}
            {galleries.filter(isShareGallery).map((gallery) => (
              <SortableGalleryCard
                key={gallery.Id}
                gallery={gallery}
                draggable={false}
                onClick={() => handleGalleryClick(gallery)}
                onEdit={openEditGalleryModal}
                onDelete={(g) => setDeleteGalleryTarget(g)}
                onOpenRanking={setShareGalleryTarget}
                onOpenBanner={() => setShowBannerModal(true)}
                onToggleShareActive={handleToggleShareActive}
                canManageGallery={permissions?.canManageGallery}
              />
            ))}
            <SortableContext items={galleries.filter((g) => !isShareGallery(g)).map((g) => g.Id)} strategy={rectSortingStrategy}>
              {galleries.filter((g) => !isShareGallery(g)).map((gallery) => (
                <SortableGalleryCard
                  key={gallery.Id}
                  gallery={gallery}
                  onClick={() => handleGalleryClick(gallery)}
                  onEdit={openEditGalleryModal}
                  onDelete={(g) => setDeleteGalleryTarget(g)}
                  onOpenRanking={setShareGalleryTarget}
                  onOpenBanner={() => setShowBannerModal(true)}
                  onToggleShareActive={handleToggleShareActive}
                  canManageGallery={permissions?.canManageGallery}
                />
              ))}
            </SortableContext>
          </div>
        </DndContext>
      )}

      {showCreateGallery && (
        <CreateGalleryModal eventId={eventId} onClose={() => setShowCreateGallery(false)} onCreated={refreshGalleries} />
      )}

      {showWatermark && <WatermarkSettingsModal onClose={() => setShowWatermark(false)} onSaved={() => {}} />}

      {shareGalleryTarget && (
        <ShareGalleryConsoleModal
          event={event}
          gallery={shareGalleryTarget}
          onClose={() => setShareGalleryTarget(null)}
        />
      )}

      {showBannerModal && (
        <SupportBannerModal eventId={eventId} onClose={() => { setShowBannerModal(false); refreshGalleries(); }} />
      )}

      {shareActiveTarget && (
        <ConfirmModal
          title={shareActiveTarget.ShareBannerActive ? t("mediaTab.confirmDeactivateShare.title") : t("mediaTab.confirmActivateShare.title")}
          message={shareActiveTarget.ShareBannerActive ? t("mediaTab.confirmDeactivateShare.message") : t("mediaTab.confirmActivateShare.message")}
          confirmText={shareActiveTarget.ShareBannerActive ? t("mediaTab.deactivate") : t("mediaTab.activate")}
          danger={shareActiveTarget.ShareBannerActive}
          loading={shareActiveLoading}
          onConfirm={confirmToggleShareActive}
          onCancel={() => !shareActiveLoading && setShareActiveTarget(null)}
        />
      )}

      {editGalleryTarget && (
        <EditNameModal
          title={t("mediaTab.editNameModal.title")}
          label={t("mediaTab.editNameModal.label")}
          value={editGalleryName}
          onChange={setEditGalleryName}
          onSave={handleEditGallery}
          onCancel={() => !actionLoading && setEditGalleryTarget(null)}
          loading={actionLoading}
          saveText={t("common:actions.save")}
        />
      )}

      {deleteGalleryTarget && (
        <ConfirmModal
          title={t("mediaTab.deleteConfirm.title")}
          message={t("mediaTab.deleteConfirm.message", { name: deleteGalleryTarget.Name })}
          confirmText={t("common:actions.delete")}
          danger
          loading={actionLoading}
          onConfirm={handleDeleteGallery}
          onCancel={() => !actionLoading && setDeleteGalleryTarget(null)}
        />
      )}
    </div>
  );
};

const s = {
  recentSection: { marginBottom: 24 },
  sectionTitle: { margin: "0 0 12px", fontSize: 15.5, fontWeight: 700, color: color.textPrimary },
  recentGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(90px, 1fr))", gap: 8 },
  recentThumb: { width: "100%", aspectRatio: "1", objectFit: "cover", borderRadius: radius.sm, background: color.gray100 },
  sectionHeader: { display: "flex", alignItems: "center", gap: 12, margin: "0 0 16px", justifyContent: "space-between", flexWrap: "wrap" },
  sectionCount: { fontSize: 12.5, color: color.textSecondary, background: color.gray100, padding: "3px 11px", borderRadius: radius.pill, fontWeight: 500 },
  savingHint: { fontSize: 12, color: color.primary600, fontWeight: 500 },
  galleryGrid: { display: "flex", flexWrap: "wrap", gap: 16 },
  galleryCard: {
    background: color.surface, borderRadius: radius.md, overflow: "hidden",
    boxShadow: shadow.sm, border: `1px solid ${color.border}`, cursor: "pointer", width: 240,
    transition: "transform 0.15s, box-shadow 0.15s",
  },
  colorBar: { height: 4 },
  galleryBody: { padding: "14px 16px 16px" },
  galleryTopRow: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10, gap: 8 },
  galleryActions: { display: "flex", alignItems: "center", gap: 6, flexShrink: 0, flexWrap: "wrap", justifyContent: "flex-end" },
  galleryName: {
    fontWeight: 700, fontSize: 14, color: color.textPrimary, flex: 1, minWidth: 0,
    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
  },
  galleryCounts: { display: "flex", gap: 14, fontSize: 12, color: color.textSecondary, marginBottom: 8 },
  countItem: { display: "inline-flex", alignItems: "center", gap: 4 },
  packageName: { display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12, color: color.primary600, marginBottom: 8, fontWeight: 500 },
  galleryMeta: { display: "flex", flexWrap: "wrap", gap: 5, marginTop: 4 },
  dragHandle: {
    display: "flex", alignItems: "center", justifyContent: "center",
    width: 18, height: 18, flexShrink: 0, color: color.gray400, cursor: "grab", marginTop: 1,
  },
  cardActionBtn: {
    display: "flex", alignItems: "center", justifyContent: "center",
    border: `1px solid ${color.borderStrong}`, background: color.surface, color: color.gray600,
    borderRadius: radius.sm, width: 24, height: 24, cursor: "pointer",
  },
  deleteActionBtn: { borderColor: color.danger50, color: color.danger500, background: color.danger50 },
  tag: { fontSize: 10, background: color.gray100, color: color.textSecondary, padding: "2px 8px", borderRadius: radius.pill, fontWeight: 500 },
  shareGalleryActions: { display: "flex", gap: 7, flexWrap: "wrap", marginTop: 13, paddingTop: 11, borderTop: `1px solid ${color.border}` },
  shareActionButton: { padding: "5px 8px", fontSize: 10.5, minHeight: 28 },
};

export default MediaTab;
